/**
 * Canvas Page - Visual canvas view for a topic
 */

import { useState, useCallback } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useTopicsStore, useCardsStore } from '@/stores'
import ContextCanvas from '@/components/ContextCanvas'
import './CanvasPage.css'

export default function CanvasPage() {
  const { topicId } = useParams<{ topicId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { getTopic, isLoading: topicsLoading } = useTopicsStore()
  const { getCardsByTopic, isLoading: cardsLoading } = useCardsStore()

  // Get focus parameter for centering on a specific card
  const focusLid = searchParams.get('focus')

  const isLoading = topicsLoading || cardsLoading

  // Multiple cards can be expanded at once (like picking up physical cards)
  const [expandedLids, setExpandedLids] = useState<Set<string>>(new Set())

  // Toggle a card's expanded state
  const handleToggleExpand = useCallback((lid: string) => {
    setExpandedLids((prev) => {
      const next = new Set(prev)
      if (next.has(lid)) {
        next.delete(lid)
      } else {
        next.add(lid)
      }
      return next
    })
  }, [])

  // Collapse all expanded cards
  const handleCollapseAll = useCallback(() => {
    setExpandedLids(new Set())
  }, [])

  const topic = topicId ? getTopic(topicId) : undefined
  const cards = topicId ? getCardsByTopic(topicId) : []

  // Handle card selection (double-click or Enter)
  const handleCardSelect = useCallback(
    (lid: string) => {
      navigate(`/card/${encodeURIComponent(lid)}`)
    },
    [navigate]
  )

  // Show loading state while data is being fetched
  if (isLoading) {
    return (
      <div className="canvas-page">
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading...</p>
        </div>
      </div>
    )
  }

  if (!topic) {
    return (
      <div className="empty-state">
        <h3>Topic not found</h3>
        <p>The topic you&apos;re looking for doesn&apos;t exist.</p>
        <Link to="/dashboard" className="btn btn-primary">
          Back to Dashboard
        </Link>
      </div>
    )
  }

  if (cards.length === 0) {
    return (
      <div className="canvas-page">
        <div className="canvas-page__header">
          <div className="canvas-page__breadcrumb">
            <Link to="/dashboard">Dashboard</Link>
            <span className="breadcrumb-sep">/</span>
            <Link to={`/topic/${topicId}`}>Topic {topic.id}</Link>
            <span className="breadcrumb-sep">/</span>
            <span>Canvas</span>
          </div>
          <h1>
            <span className="topic-id-badge">{topic.id}/</span>
            {topic.name}
          </h1>
        </div>
        <div className="empty-state">
          <h3>No cards to display</h3>
          <p>Create some cards in this topic first.</p>
          <Link to={`/card/new?topic=${topicId}`} className="btn btn-primary">
            Create First Card
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="canvas-page">
      <div className="canvas-page__header">
        <div className="canvas-page__title-row">
          <div>
            <div className="canvas-page__breadcrumb">
              <Link to="/dashboard">Dashboard</Link>
              <span className="breadcrumb-sep">/</span>
              <Link to={`/topic/${topicId}`}>Topic {topic.id}</Link>
              <span className="breadcrumb-sep">/</span>
              <span>Canvas</span>
            </div>
            <h1>
              <span className="topic-id-badge">{topic.id}/</span>
              {topic.name}
            </h1>
          </div>
          <div className="canvas-page__actions">
            <Link to={`/topic/${topicId}`} className="btn btn-secondary">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 6h18M3 12h18M3 18h18" />
              </svg>
              List View
            </Link>
            <Link to={`/card/new?topic=${topicId}`} className="btn btn-primary">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M12 5v14M5 12h14" />
              </svg>
              New Card
            </Link>
          </div>
        </div>
        <div className="canvas-page__stats">
          <span>{cards.length} cards</span>
          {expandedLids.size > 0 && (
            <>
              <span className="stat-sep">|</span>
              <span>
                <strong>{expandedLids.size}</strong> expanded
              </span>
            </>
          )}
        </div>
      </div>

      <div className="canvas-page__content">
        <ContextCanvas
          cards={cards}
          topic={topic}
          expandedLids={expandedLids}
          onToggleExpand={handleToggleExpand}
          onCollapseAll={handleCollapseAll}
          onCardOpen={handleCardSelect}
          focusLid={focusLid}
        />
      </div>
    </div>
  )
}
